import { FC } from "react";
import { VBox } from "@/components-v2/Box";
import { LinkButton } from "@/components-v2/Button";
import { AssignsNewCompanyBoardForm } from "./assignsNewCompanyBoardDomain";
import { YesNoQuestion } from "@/dbot/common/commonComponents";
import { observer } from "mobx-react";
import { Alert } from "@/components-v2/Alert";
import { Checkbox } from "@/components-v2/Checkbox";
import { Dialog } from "@/components-v2/Dialog";
import { MTextInput } from "@/components-v2/TextInput";
import { Select } from "@/components-v2/Select";

import { useTranslation, useUserStore } from "@/stores/RootStoreContext";
import { isNorwegianOrgNo } from "@/utils/orgNo";
import { getFullName } from "@/utils/Roles";
import { adminRoles } from "@/utils/navigation";

export const AssignsNewCompanyBoardView: FC<{
  form: AssignsNewCompanyBoardForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  const userStore = useUserStore();

  return (
    <VBox gap={"md"}>
      <VBox>
        {isNorwegianOrgNo(userStore.orgNumber) && (
          <VBox marginBottom={"lg"}>
            <YesNoQuestion
              question={t("decisions.companyBoard.board-election.gender-balance", {
                companyName: userStore.companyName,
              })}
              field={form.genderCompositionRequirement}
              isLocked={form.isProjectLocked}
            />
            <Alert
              message={{ key: "decisions.companyBoard.board-election.gender-balance-info" }}
              type={"info"}
            />
          </VBox>
        )}
        <div>{t("decisions.companyBoard.board-election.who-board-members")}</div>
        {form.newBoardMembers.value.map((companyBoardMember) => {
          return companyBoardMember ? (
            <Checkbox
              key={companyBoardMember.id}
              label={companyBoardMember.name}
              onChange={() => form.toggleNewBoardMember(companyBoardMember.id)}
              checked={companyBoardMember.isSelected}
              disabled={form.isProjectLocked}
            />
          ) : null;
        })}
        {form.newBoardMembersByName?.value.map((companyBoardMemberByName) => {
          return (
            <Checkbox
              key={companyBoardMemberByName.id}
              label={companyBoardMemberByName.name}
              onChange={() => form.toggleNewBoardMemberByName(companyBoardMemberByName.id)}
              checked={companyBoardMemberByName.isSelected}
              disabled={form.isProjectLocked}
            />
          );
        })}
      </VBox>
      {form.showNoBoardMembersSelectedText && (
        <Alert type="info" message={t("decisions.companyBoard.board-election.no-board-members")} />
      )}
      {form.showSingleBoardMemberText && (
        <Alert
          type="info"
          message={t("decisions.companyBoard.board-election.only-one-board-member")}
        />
      )}
      <div>
        <LinkButton
          label={t("decisions.companyBoard.board-election.add-board-member")}
          icon={"plus"}
          onClick={form.openAddNewBoardMemberDialog}
          disabled={form.isProjectLocked}
        />
      </div>
      {form.isNorwegianOrgNo && form.boardMembersWithMissingSsn.length > 0 && (
        <Alert
          message={{
            key: "decisions.companyBoard.board-election.missing-ssn-board-member",
            params: {
              boardMembers: {
                type: "list",
                value: form.boardMembersWithMissingSsn.map((r) => getFullName(r)),
              },
              link: adminRoles(form.orgNo),
            },
          }}
          type={"warning"}
        />
      )}
      {form.addNewBoardMemberModel && (
        <Dialog
          title={t("decisions.companyBoard.board-election.add-board-member")}
          submitLabel={t("decisions.save")}
          onSubmit={form.addNewBoardMember}
          onCancel={form.closeAddNewBoardMemberDialog}
        >
          <MTextInput
            label={t("decisions.name")}
            name={"name"}
            model={form.addNewBoardMemberModel.name}
            autofocus
          />
        </Dialog>
      )}
      {form.showSelectChairpersonQuestion && (
        <YesNoQuestion
          question={t("decisions.companyBoard.board-election.select-chairperson")}
          field={form.selectChairPerson}
          isLocked={form.isProjectLocked}
        />
      )}
      {form.showSelectChairPersonSelect && (
        <>
          <Select
            label={t("decisions.companyBoard.board-election.select-chairperson-label")}
            value={form.selectedChairperson.value}
            onSelect={form.selectedChairperson.setValue}
            items={form.selectableBoardMembers}
            disabled={form.isProjectLocked}
            idFn={(item) => item.id ?? ""}
            labelFn={(item) => item.name ?? ""}
          />
          {form.selectedChairperson.value && (
            <YesNoQuestion
              question={t("decisions.companyBoard.board-election.select-deputy-chairperson")}
              field={form.selectDeputyChairperson}
              isLocked={form.isProjectLocked}
            />
          )}
        </>
      )}
      {form.showSelectDeputyChairpersonQuestion && (
        <Select
          label={t("decisions.companyBoard.board-election.select-deputy-chairperson-label")}
          value={form.selectedDeputyChairperson.value}
          onSelect={form.selectedDeputyChairperson.setValue}
          items={form.selectableBoardMembersExceptChairperson}
          disabled={form.isProjectLocked}
          idFn={(item) => item.id ?? ""}
          labelFn={(item) => item.name ?? ""}
        />
      )}
      <VBox>
        <div>{t("decisions.companyBoard.board-election.who-vara-board-members")}</div>
        {form.newDeputyBoardMembers.value.map((newDeputyBoardMember) => {
          return (
            <VBox key={newDeputyBoardMember.id}>
              <Checkbox
                label={newDeputyBoardMember.name ?? ""}
                onChange={() => form.newDeputyBoardMembers.toggleSelected(newDeputyBoardMember.id)}
                checked={newDeputyBoardMember.isSelected}
                disabled={form.isProjectLocked}
              />
              {newDeputyBoardMember.isSelected && form.numberOfSelectedBoardMembers > 0 && (
                <VBox marginLeft={"md"}>
                  <Checkbox
                    label={t("decisions.companyBoard.board-election.personal-vara")}
                    onChange={() =>
                      form.newDeputyBoardMembers.togglePersonalDeputy(newDeputyBoardMember.id)
                    }
                    checked={newDeputyBoardMember.isPersonal}
                    disabled={form.isProjectLocked}
                  />
                  {newDeputyBoardMember.isPersonal &&
                    form.selectableBoardMembers?.map((companyBoardMember) => {
                      return (
                        <Checkbox
                          key={
                            newDeputyBoardMember.id +
                            "-personal-deputy-for-" +
                            companyBoardMember.id
                          }
                          label={companyBoardMember.name}
                          onChange={() =>
                            form.newDeputyBoardMembers.togglePersonalDeputyFor(
                              newDeputyBoardMember.id,
                              companyBoardMember,
                            )
                          }
                          checked={newDeputyBoardMember.personalDeputyFor?.some(
                            (m) => m.id === companyBoardMember.id,
                          )}
                          disabled={form.isProjectLocked}
                        />
                      );
                    })}
                </VBox>
              )}
            </VBox>
          );
        })}
        {form.newDeputyBoardMembersByName.value.map((newDeputyBoardMember) => {
          return (
            <VBox key={newDeputyBoardMember.id}>
              <Checkbox
                label={newDeputyBoardMember.name ?? ""}
                onChange={() =>
                  form.newDeputyBoardMembersByName.toggleSelected(newDeputyBoardMember.id)
                }
                checked={newDeputyBoardMember.isSelected}
                disabled={form.isProjectLocked}
              />
              {newDeputyBoardMember.isSelected && form.numberOfSelectedBoardMembers > 0 && (
                <VBox key={newDeputyBoardMember.id + "_personal"} marginLeft={"md"}>
                  <Checkbox
                    label={t("decisions.companyBoard.board-election.personal-vara")}
                    onChange={() =>
                      form.newDeputyBoardMembersByName.togglePersonalDeputy(newDeputyBoardMember.id)
                    }
                    checked={newDeputyBoardMember.isPersonal}
                    disabled={form.isProjectLocked}
                  />
                  {newDeputyBoardMember.isPersonal &&
                    form.selectableBoardMembers?.map((companyBoardMember) => {
                      return (
                        <Checkbox
                          key={
                            newDeputyBoardMember.id +
                            "-personal-name-deputy-for-" +
                            companyBoardMember.id
                          }
                          label={companyBoardMember.name}
                          onChange={() =>
                            form.newDeputyBoardMembersByName.togglePersonalDeputyFor(
                              newDeputyBoardMember.id,
                              companyBoardMember,
                            )
                          }
                          checked={newDeputyBoardMember.personalDeputyFor?.some(
                            (m) => m.id === companyBoardMember.id,
                          )}
                          disabled={form.isProjectLocked}
                        />
                      );
                    })}
                </VBox>
              )}
            </VBox>
          );
        })}
        <div>
          <LinkButton
            label={t("decisions.companyBoard.board-election.add-deputy-board-member")}
            icon={"plus"}
            onClick={form.openAddNewBoardDeputyDialog}
            disabled={form.isProjectLocked}
          />
        </div>
        {form.isNorwegianOrgNo && form.deputyMembersWithMissingSsn.length > 0 && (
          <Alert
            message={{
              key: "decisions.companyBoard.board-election.missing-ssn-board-member",
              params: {
                boardMembers: {
                  type: "list",
                  value: form.deputyMembersWithMissingSsn.map((r) => getFullName(r)),
                },
                link: adminRoles(form.orgNo),
              },
            }}
            type={"warning"}
          />
        )}
        {form.addNewBoardDeputyModel && (
          <Dialog
            title={t("decisions.companyBoard.board-election.add-deputy-board-member")}
            submitLabel={t("decisions.save")}
            onSubmit={form.addNewBoardDeputy}
            onCancel={form.closeAddNewBoardDeputyDialog}
          >
            <MTextInput
              label={t("decisions.name")}
              name={"name"}
              model={form.addNewBoardDeputyModel.name}
            />
          </Dialog>
        )}
        <YesNoQuestion
          question={t("documents.boardMeeting.boardMeetingProtocolShowCompanyBoard")}
          field={form.boardMeetingProtocolShowCompanyBoard}
          isLocked={form.isProjectLocked}
        />
        <YesNoQuestion
          question={t("documents.noticeToGM.noticeGeneralMeetingShowCompanyBoard")}
          field={form.noticeGeneralMeetingShowCompanyBoard}
          isLocked={form.isProjectLocked}
        />
      </VBox>
    </VBox>
  );
});
